import { tx as translateInterfaceText } from '@/utils/i18n';
/**
 * Speed Test Service - Integração com API de Testes de Velocidade
 *
 * Gerencia requisições relacionadas aos testes de velocidade de vídeos.
 * Permite criar, deletar, listar e obter métricas dos testes.
 *
 * Rotas do Backend:
 * - POST /speed-test/:videoId - Criar teste de velocidade
 * - DELETE /speed-test/:speedTestId - Deletar teste
 * - GET /speed-test/:userId - Listar testes por usuário
 * - GET /speed-test/:speedTestId/videos - Selecionar vídeo do teste
 * - GET /speed-test/:speedTestId/metrics - Obter métricas do teste
 */

import { api } from './api';

// ===== TIPOS =====

export interface CreateSpeedTestRequest {
  videoId: string;
  userId: string;
  speeds: number[]; // Array de velocidades a serem testadas (ex: [1.0, 1.2, 1.5])
  endDate?: string;
}

export interface SpeedTestResponse {
  id: string;
  id_speed_test?: string;
  videoId: string;
  userId: string;
  speeds: number[];
  createdAt: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
  enabled?: boolean;
  isAutomatic?: boolean;
  testStatus?: 'running' | 'completed' | 'paused' | 'pending';
  date?: string;
}

export interface SpeedTestVideo {
  videoId: string;
  speed: number;
  // Adicione outros campos conforme retornado pela API
}

export interface SpeedTestMetrics {
  videoId: string;
  speed: number;
  views?: number;
  uniqueViews?: number;
  plays?: number;
  uniquePlays?: number;
  buttonClicks?: number;
  playRate?: number;
  conversionRate?: number;
  engagement?: number;
  retentionData?: Array<{
    timestamp: number;
    percentage: number;
  }>;
}

export interface GetMetricsParams {
  speedTestId: string;
  inicio: string; // Data inicial no formato ISO ou timestamp
  fim: string;    // Data final no formato ISO ou timestamp
}

// ===== SERVIÇO =====

export const speedTestService = {
  /**
   * Cria um novo teste de velocidade
   *
   * @param videoId - ID do vídeo a ser testado
   * @param userId - ID do usuário criador
   * @param speeds - Array de velocidades a serem testadas (ex: [1.0, 1.2, 1.5])
   * @returns Promise com mensagem de sucesso
   */
  createSpeedTest: async (
    videoId: string,
    userId: string,
    speeds: number[],
    endDate?: string
  ): Promise<{ message: string; id_speed_test: string }> => {
    try {
      const response = await api.post(`/speed-test/${videoId}`, {
        userId,
        speeds,
        endDate,
      });
      return response;
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || translateInterfaceText("Erro ao criar teste de velocidade"));
    }
  },

  /**
   * Deleta um teste de velocidade
   *
   * @param speedTestId - ID do teste a ser deletado
   * @returns Promise vazia (status 204)
   */
  deleteSpeedTest: async (speedTestId: string): Promise<void> => {
    try {
      await api.delete(`/speed-test/${speedTestId}`);
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || translateInterfaceText("Erro ao deletar teste de velocidade"));
    }
  },

  /**
   * Lista todos os testes de velocidade de um usuário
   *
   * @param userId - ID do usuário
   * @returns Promise com array de testes
   */
  findSpeedTestsByUserId: async (userId: string): Promise<SpeedTestResponse[]> => {
    try {
      const response = await api.get(`/speed-test/${userId}`);
      return response;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return []; // Retorna array vazio se não houver testes
      }
      throw new Error(error?.response?.data?.message || translateInterfaceText("Erro ao buscar testes de velocidade"));
    }
  },

  /**
   * Seleciona o vídeo vencedor do teste de velocidade
   * Usado para distribuição de tráfego baseada no teste
   *
   * @param speedTestId - ID do teste
   * @returns Promise com dados do vídeo selecionado
   */
  selectSpeedTestVideo: async (speedTestId: string): Promise<{ data: SpeedTestVideo }> => {
    try {
      const response = await api.get(`/speed-test/${speedTestId}/videos`);
      return response;
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || translateInterfaceText("Erro ao selecionar vídeo do teste"));
    }
  },

  /**
   * Obtém métricas detalhadas dos vídeos em teste
   *
   * @param params - Parâmetros contendo speedTestId, data inicial e final
   * @returns Promise com array de métricas por velocidade
   */
  getSpeedTestMetrics: async (params: GetMetricsParams): Promise<{
    message: string;
    data: SpeedTestMetrics[];
  }> => {
    try {
      const { speedTestId, inicio, fim } = params;
      const response = await api.get(
        `/speed-test/${speedTestId}/metrics`,
        {
          params: { inicio, fim },
        }
      );
      return response;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        throw new Error(translateInterfaceText("Data inicial e data final são obrigatórias"));
      }
      throw new Error(error?.response?.data?.message || translateInterfaceText("Erro ao buscar métricas do teste"));
    }
  },
};

export default speedTestService;
